import React, { useState } from "react";
import config from "../../Config";
import axios from "axios";
import { NavLink } from "react-router-dom";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import Spinner from "react-bootstrap/Spinner";
import LibraryMenu from "./LibraryMenu";
import * as XLSX from "xlsx";

const TEMPLATE_HEADERS = [
    "Book_No",
    "Book_Name",
    "Writer_Name",
    "Publisher_Name",
    "Category",
    "Edition",
    "Total_Pages",
    "Binding_Type",
    "Price",
    "Jild_No",
    "Purchasing_Source",
    "Remarks",
];

const ImportBooks = () => {
    const [AccessKey] = useState(localStorage.getItem("AccessKey"));
    const [UserID] = useState(localStorage.getItem("ID"));
    const [MemberType] = useState(localStorage.getItem("MemberType"));
    const [loading, setLoading] = useState(false);
    const [importErrors, setImportErrors] = useState([]);
    const [fileName, setFileName] = useState("");

    document.title = "Import Library Books";

    const downloadTemplate = () => {
        const ws = XLSX.utils.aoa_to_sheet([
            TEMPLATE_HEADERS,
            ["B001", "Sample Book", "Author Name", "Publisher", "Fiction", "1st", "200", "Hard Binding", "500", "1", "Purchase", ""],
        ]);
        const wb = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(wb, ws, "Books");
        XLSX.writeFile(wb, "library_books_import_template.xlsx");
    };

    const normalizeRow = (row) => ({
        Book_No: String(row.Book_No || row.book_no || "").trim(),
        Book_Name: String(row.Book_Name || row.book_name || "").trim(),
        Writer_Name: String(row.Writer_Name || row.writer_name || "").trim(),
        Publisher_Name: String(row.Publisher_Name || row.publisher_name || "").trim(),
        Category: String(row.Category || row.category || "").trim(),
        Edition: String(row.Edition || row.edition || "").trim(),
        Total_Pages: parseInt(row.Total_Pages || row.total_pages || 0, 10) || 0,
        Binding_Type: String(row.Binding_Type || row.binding_type || "Not Available").trim(),
        Price: parseInt(row.Price || row.price || 0, 10) || 0,
        Jild_No: String(row.Jild_No || row.jild_no || "").trim(),
        Purchasing_Source: String(row.Purchasing_Source || row.purchasing_source || "").trim(),
        Remarks: String(row.Remarks || row.remarks || "").trim(),
    });

    const handleFile = (event) => {
        const file = event.target.files && event.target.files[0];
        if (!file) return;

        setFileName(file.name);
        setImportErrors([]);
        setLoading(true);

        const reader = new FileReader();
        reader.onload = (ev) => {
            try {
                const workbook = XLSX.read(ev.target.result, { type: "array" });
                const sheet = workbook.Sheets[workbook.SheetNames[0]];
                const rows = XLSX.utils.sheet_to_json(sheet);
                const books = rows.map(normalizeRow).filter((r) => r.Book_No || r.Book_Name);

                if (books.length === 0) {
                    setLoading(false);
                    toast.error("No valid rows found in file.", { position: toast.POSITION.TOP_RIGHT });
                    return;
                }

                axios({
                    method: "post",
                    url: config.base_url + "Library/ImportLibraryBooks",
                    headers: { "Content-Type": "application/json" },
                    data: {
                        AccessKey: AccessKey,
                        UserID: UserID,
                        MemberType: MemberType,
                        LibraryBooks: books,
                    },
                })
                    .then(function (response) {
                        setLoading(false);
                        if (response.data.status_code === 1) {
                            toast.success(response.data.status_message, { position: toast.POSITION.TOP_RIGHT });
                            setImportErrors(response.data.Errors || []);
                        } else {
                            toast.error(response.data.status_message || "Import failed.", {
                                position: toast.POSITION.TOP_RIGHT,
                            });
                        }
                    })
                    .catch(function () {
                        setLoading(false);
                        toast.error("Network Error..", { position: toast.POSITION.TOP_RIGHT });
                    });
            } catch (err) {
                setLoading(false);
                toast.error("Could not read file. Use the Excel template.", {
                    position: toast.POSITION.TOP_RIGHT,
                });
            }
        };
        reader.readAsArrayBuffer(file);
        event.target.value = "";
    };

    return (
        <>
            <LibraryMenu />
            <ToastContainer />
            <div className="page-head">
                <div className="container">
                    <div className="row align-items-center">
                        <div className="col-md-6">
                            <nav aria-label="breadcrumb">
                                <ol className="breadcrumb">
                                    <li className="breadcrumb-item"><NavLink to="/Dashboard">Dashboard</NavLink></li>
                                    <li className="breadcrumb-item"><NavLink to="/Library/LibraryDashboard">Library</NavLink></li>
                                    <li className="breadcrumb-item active">Import Books</li>
                                </ol>
                            </nav>
                            <div className="page-title">Import Books</div>
                        </div>
                        <div className="col-md-6 text-end">
                            <NavLink to="/Library/ViewLibrary" className="button button-white button-icon">
                                Manage Library
                                <i className="ri-file-list-2-line"></i>
                            </NavLink>
                        </div>
                    </div>
                </div>
            </div>

            <div className="page-content">
                <div className="container">
                    <div className="card">
                        <div className="card-body p-md-4">
                            <p className="mb-3">
                                Upload an Excel file using the template columns below. Book No must be unique.
                                Imported books are saved as <strong>Available</strong>.
                            </p>
                            <div className="row gy-3 align-items-end">
                                <div className="col-md-4">
                                    <button type="button" className="button button-white w-100" onClick={downloadTemplate}>
                                        Download Template
                                    </button>
                                </div>
                                <div className="col-md-4">
                                    <label className="form-label">Select Excel file</label>
                                    <input type="file" className="form-control" accept=".xlsx,.xls" onChange={handleFile} />
                                    {fileName && <small className="text-muted">{fileName}</small>}
                                </div>
                                <div className="col-md-4">
                                    {loading && (
                                        <div className="text-center">
                                            <Spinner animation="border" role="status" />
                                        </div>
                                    )}
                                </div>
                            </div>

                            <div className="mt-4">
                                <h6>Expected columns</h6>
                                <p className="small text-muted mb-0">{TEMPLATE_HEADERS.join(", ")}</p>
                            </div>

                            {importErrors.length > 0 && (
                                <div className="alert alert-warning mt-4 mb-0">
                                    <strong>Import notes</strong>
                                    <ul className="mb-0 mt-2">
                                        {importErrors.map((msg, idx) => (
                                            <li key={idx}>{msg}</li>
                                        ))}
                                    </ul>
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            </div>
        </>
    );
};

export default ImportBooks;
